import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { Toast } from '../../../components';
import useToast from '../../../hooks/useToast';
import { format, startOfMonth } from 'date-fns';
import Visits from './Visits';
import { updateProductStock } from '../../../utils/stockUtils';
import { useAuth } from '../../../contexts/AuthContext';

function VisitsContainer() {
  const { role } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [doctorPage, setDoctorPage] = useState(1);
  const [doctorPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [creatorOptions, setCreatorOptions] = useState([]);
  const [doctorVisitCounts, setDoctorVisitCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(false);
  const { toast, showSuccess, showError, hideToast } = useToast();
  const [allVisits, setAllVisits] = useState([]);
  const canViewSales = role === 'owner';
  const showVisitStats = role === 'owner';
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Reset pages when filters change
    setPage(1);
    setDoctorPage(1);
  }, [searchTerm, cityFilter, creatorFilter]);

  useEffect(() => {
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter, cityFilter, creatorFilter, canViewSales]);

  useEffect(() => {
    fetchDoctorVisitCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter, cityFilter, showVisitStats]);

  useEffect(() => {
    fetchCityOptions();
    fetchCreatorOptions();
  }, []);

  const fetchCreatorOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name')
        .order('display_name');

      if (error) {
        console.error('Error fetching creator options:', error);
        return;
      }

      const uniqueCreators = [...new Set(
        data
          .map(profile => profile.display_name)
          .filter(name => name && name.trim() !== '')
      )].sort();

      setCreatorOptions(uniqueCreators);
    } catch (error) {
      console.error('Error fetching creator options:', error);
    }
  };

  const fetchCityOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('address')
        .order('address');

      if (error) {
        console.error('Error fetching city options:', error);
        return;
      }

      const uniqueCities = [...new Set(
        data
          .map(doctor => doctor.address)
          .filter(city => city && city.trim() !== '')
          .map(city => city.trim())
      )].sort();

      setCityOptions(uniqueCities);
    } catch (error) {
      console.error('Error fetching city options:', error);
    }
  };

  const fetchVisits = async () => {
    try {
      setLoading(true);

      const justStart = !!(startDate && !endDate);
      const justEnd = !!(!startDate && endDate);
      const invalidRange = !!(startDate && endDate && endDate < startDate);

      if (justStart || justEnd || invalidRange) {
        if (isMountedRef.current) {
          setAllVisits([]);
          setTotalCount(0);
        }
        return;
      }

      const selectFields = canViewSales
        ? `
          *,
          doctors (name, specialization, hospital, address, contact_type),
          sales (
            id,
            quantity,
            total_amount,
            products (name)
          )
        `
        : `
          *,
          doctors (name, specialization, hospital, address, contact_type)
        `;

      let query = supabase
        .from('visits')
        .select(selectFields)
        .order('visit_date', { ascending: false });

      if (startDate && endDate) {
        query = query.gte('visit_date', startDate).lte('visit_date', endDate);
      }

      if (statusFilter === 'completed') {
        query = query.eq('status', 'completed');
      } else if (statusFilter === 'other') {
        query = query.neq('status', 'completed');
      }

      const { data, error } = await query;

      if (!isMountedRef.current) return;

      if (error) {
        showError('Failed to load visits. Please check your connection and try again.');
        setAllVisits([]);
        setTotalCount(0);
        return;
      }

      let filteredData = data || [];

      // Fetch creator profile info for each visit
      if (filteredData.length > 0) {
        const userIds = [...new Set(filteredData.map(v => v.user_id).filter(Boolean))];
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', userIds);
          
          if (profiles) {
            const profileMap = profiles.reduce((acc, p) => {
              acc[p.id] = p.display_name;
              return acc;
            }, {});
            
            filteredData = filteredData.map(visit => ({
              ...visit,
              created_by: profileMap[visit.user_id] || 'Unknown'
            }));
          }
        }
      }

      if (cityFilter) {
        filteredData = filteredData.filter(visit => {
          const visitCity = visit.doctors?.address?.trim();
          const selectedCity = cityFilter.trim();
          return visitCity === selectedCity;
        });
      }

      if (creatorFilter && creatorFilter !== 'all') {
        filteredData = filteredData.filter(visit => 
          visit.created_by === creatorFilter
        );
      }

      if (!isMountedRef.current) return;
      setAllVisits(filteredData);
      setTotalCount(filteredData.length);

    } catch (error) {
      if (!isMountedRef.current) return;
      showError('Error loading visits. Please try again.');
      setAllVisits([]);
      setTotalCount(0);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchDoctorVisitCounts = async () => {
    try {
      if (!showVisitStats) {
        if (isMountedRef.current) setDoctorVisitCounts([]);
        return;
      }

      const justStart = !!(startDate && !endDate);
      const justEnd = !!(!startDate && endDate);
      const invalidRange = !!(startDate && endDate && endDate < startDate);

      if (justStart || justEnd || invalidRange) {
        if (isMountedRef.current) setDoctorVisitCounts([]);
        return;
      }

      if (isMountedRef.current) setCountsLoading(true);

      // Step 1: Get ALL doctors first, then filter by city if needed
      let doctorsQuery = supabase
        .from('doctors')
        .select('id, name, specialization, hospital, address, contact_type')
        .order('name');

      const { data: allDoctors, error: doctorsError } = await doctorsQuery;

      if (doctorsError) {
        showError('Database error fetching doctors');
        return;
      }

      // Filter doctors by city on the client side
      let filteredDoctors = allDoctors;
      if (cityFilter) {
        filteredDoctors = allDoctors.filter(doctor => {
          const doctorCity = doctor.address?.trim();
          const selectedCity = cityFilter.trim();
          return doctorCity === selectedCity;
        });
      }

      // Step 2: Get visit counts and last visit date for the date range
      let visitQuery = supabase
        .from('visits')
        .select('doctor_id, status, visit_date')
        .order('visit_date', { ascending: false });

      if (startDate && endDate) {
        visitQuery = visitQuery.gte('visit_date', startDate).lte('visit_date', endDate);
      }

      if (statusFilter === 'completed') {
        visitQuery = visitQuery.eq('status', 'completed');
      } else if (statusFilter === 'other') {
        visitQuery = visitQuery.neq('status', 'completed');
      }

      const { data: visits, error: visitsError } = await visitQuery;

      if (visitsError) {
        showError('Database error fetching visit counts');
        return;
      }

      // Step 3: Create maps for doctor visit counts and last visit dates
      const visitCountMap = new Map();
      const lastVisitDateMap = new Map();
      (visits || []).forEach((visit) => {
        if (!visit.doctor_id) return;
        const count = visitCountMap.get(visit.doctor_id) || 0;
        visitCountMap.set(visit.doctor_id, count + 1);
        
        // Set last visit date (only if not already set, since visits are ordered by date DESC)
        if (!lastVisitDateMap.has(visit.doctor_id)) {
          lastVisitDateMap.set(visit.doctor_id, visit.visit_date);
        }
      });

      // Step 4: Combine filtered doctors with their visit counts and last visit dates
      const doctorVisitCounts = filteredDoctors.map(doctor => ({
        doctor_id: doctor.id,
        doctor: {
          name: doctor.name,
          specialization: doctor.specialization,
          hospital: doctor.hospital,
          city: doctor.address,
          contact_type: doctor.contact_type
        },
        count: visitCountMap.get(doctor.id) || 0,
        lastVisitDate: lastVisitDateMap.get(doctor.id) || null
      }));

      // Sort by visit count (descending), then by name
      const sortedCounts = doctorVisitCounts.sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return (a.doctor?.name || '').localeCompare(b.doctor?.name || '');
      });

      if (!isMountedRef.current) return;
      setDoctorVisitCounts(sortedCounts);
    } catch (error) {
      if (!isMountedRef.current) return;
      showError('Error loading visit statistics. Please try again.');
      setDoctorVisitCounts([]);
    } finally {
      if (isMountedRef.current) setCountsLoading(false);
    }
  };

  const deleteVisit = async (id) => {
    if (window.confirm('Are you sure you want to delete this visit? This will also delete all associated sales.')) {
      try {
        // Get visit details for confirmation message
        const { data: visitData } = await supabase
          .from('visits')
          .select(`
            visit_date,
            doctor_id,
            doctors (name, contact_type),
            sales (id, product_id, quantity, total_amount, products (name))
          `)
          .eq('id', id)
          .single();

        if (!visitData) {
          showError('Visit not found');
          return;
        }

        const salesCount = visitData?.sales?.length || 0;
        const totalAmount = visitData?.sales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0;

        // Delete ALL ledger entries for this visit
        if (totalAmount > 0) {
          const { error: deleteLedgerError } = await supabase
            .from('ledger_entries')
            .delete()
            .eq('source_type', 'visit')
            .eq('source_id', id);

          if (deleteLedgerError) {
            console.error('Ledger deletion error:', deleteLedgerError);
            showError('Failed to delete ledger entry. Deletion aborted.');
            return;
          }
        }

        // Delete original stock transactions and add stock back
        if (visitData?.sales) {
          for (const sale of visitData.sales) {
            try {
              // First, delete the original stock transaction for this sale
              const { error: deleteStockError } = await supabase
                .from('stock_transactions')
                .delete()
                .eq('reference_type', 'visit')
                .eq('reference_id', id)
                .eq('product_id', sale.product_id);

              if (deleteStockError) {
                console.error('Stock transaction deletion error:', deleteStockError);
                showError(`Failed to delete stock transaction for ${sale.products?.name}. Deletion aborted.`);
                return;
              }
            } catch (stockError) {
              console.error('Stock deletion error:', stockError);
              showError(`Failed to reverse stock for ${sale.products?.name}. Deletion aborted.`);
              return;
            }
          }
        }

        // Delete the visit (cascade will delete sales)
        const { error } = await supabase
          .from('visits')
          .delete()
          .eq('id', id);

        if (error) {
          showError('Failed to delete visit. Please try again.');
          return;
        }

        // Update current stock for all affected products
        if (visitData?.sales) {
          for (const sale of visitData.sales) {
            try {
              await updateProductStock(sale.product_id);
            } catch (stockError) {
              console.error('Product stock update error:', stockError);
            }
          }
        }

        // Show detailed success message
        const isChemist = visitData?.doctors?.contact_type === 'chemist';
        const contactName = visitData?.doctors?.name || 'Unknown Contact';
        const visitDate = visitData?.visit_date ? format(new Date(visitData.visit_date), 'MMM dd, yyyy') : '';

        let successMessage = `Visit deleted successfully`;
        if (contactName && visitDate) {
          successMessage += ` (${contactName}${isChemist ? ' [Chemist]' : ''} - ${visitDate})`;
        }
        if (salesCount > 0) {
          successMessage += `. ${salesCount} sale${salesCount !== 1 ? 's' : ''} and stock transactions removed`;
        }

        showSuccess(successMessage);

        // Refresh data
        await fetchVisits();
        await fetchDoctorVisitCounts();

      } catch (error) {
        console.error('Error deleting visit:', error);
        showError('Error deleting visit. Please try again.');
      }
    }
  };

  const calculateTotalSales = (sales) => {
    return sales?.reduce((total, sale) => total + parseFloat(sale.total_amount), 0) || 0;
  };

  // Apply search filtering
  const filteredVisits = allVisits.filter(visit => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const isChemist = visit.doctors?.contact_type === 'chemist';
    
    return (
      (visit.doctors?.name || '').toLowerCase().includes(searchLower) ||
      (!isChemist && (visit.doctors?.specialization || '').toLowerCase().includes(searchLower)) ||
      (visit.doctors?.hospital || '').toLowerCase().includes(searchLower) ||
      (visit.status || '').toLowerCase().includes(searchLower) ||
      (visit.notes || '').toLowerCase().includes(searchLower)
    );
  });

  // Apply pagination to filtered results
  const paginatedVisits = filteredVisits.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const totalFilteredCount = filteredVisits.length;
  const maxPage = Math.max(1, Math.ceil(totalFilteredCount / pageSize));

  return (
    <>
      <Visits
        loading={loading}
        role={role}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        totalCount={totalCount}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        cityOptions={cityOptions}
        creatorFilter={creatorFilter}
        setCreatorFilter={setCreatorFilter}
        creatorOptions={creatorOptions}
        doctorVisitCounts={doctorVisitCounts}
        countsLoading={countsLoading}
        doctorPage={doctorPage}
        setDoctorPage={setDoctorPage}
        doctorPageSize={doctorPageSize}
        deleteVisit={deleteVisit}
        calculateTotalSales={calculateTotalSales}
        filteredVisits={paginatedVisits}
        totalFilteredCount={totalFilteredCount}
        maxPage={maxPage}
        showSales={canViewSales}
      />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}

export default VisitsContainer;